import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getLipSyncHealth, startLipSyncServer, getLipSyncAvatars, getLipSyncAudios,
  uploadLipSyncImage, startLipSyncGeneration, getLipSyncTask, cancelLipSyncTask,
  lipSyncFileUrl,
} from '../../services/lipSyncService';

const DEV = typeof import.meta !== 'undefined' && !!import.meta.env?.DEV;

const S = {
  card: {
    background: 'rgba(10, 25, 41, 0.6)', border: '1px solid rgba(197,160,89,0.18)',
    borderRadius: '14px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px',
  },
  label: {
    fontSize: '0.7rem', fontWeight: 700, color: '#c5a059', textTransform: 'uppercase',
    letterSpacing: '0.06em', marginBottom: '4px', display: 'block',
  },
  input: {
    width: '100%', background: 'rgba(7, 21, 38, 0.8)', border: '1px solid rgba(197,160,89,0.25)',
    borderRadius: '8px', padding: '8px 12px', fontSize: '0.8rem', color: '#e2e8f0',
    outline: 'none', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif",
  },
  select: {
    width: '100%', background: 'rgba(7, 21, 38, 0.8)', border: '1px solid rgba(197,160,89,0.25)',
    borderRadius: '8px', padding: '8px 10px', fontSize: '0.8rem', color: '#fff', outline: 'none',
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #8a6a2e 0%, #db2777 100%)', color: '#fff', border: 'none',
    borderRadius: '10px', padding: '9px 18px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '7px', whiteSpace: 'nowrap',
  },
  btnSecondary: {
    background: 'rgba(197,160,89,0.1)', color: '#c5a059', border: '1px solid rgba(197,160,89,0.3)',
    borderRadius: '10px', padding: '8px 14px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '7px', whiteSpace: 'nowrap',
  },
  btnDanger: {
    background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '10px', padding: '8px 12px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
  },
  badge: (color) => ({
    display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 9px', borderRadius: '20px',
    fontSize: '0.65rem', fontWeight: 700, background: `${color}22`, color, border: `1px solid ${color}44`,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  }),
};

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_{2,}/g, '_')
    .slice(0, 80) || 'lipsync_video';

const kb = (n) => (n ? `${(n / 1024).toFixed(0)} KB` : '');

const AdminLipSync = () => {
  const [health, setHealth] = useState(null);
  const [caps, setCaps] = useState(null);
  const [groups, setGroups] = useState([]);
  const [audios, setAudios] = useState([]);
  const [activeFolder, setActiveFolder] = useState('');

  const [image, setImage] = useState(null); // {name,url,path}
  const [audio, setAudio] = useState(null); // {name,url,path}
  const [fps, setFps] = useState(25);
  const [bboxShift, setBboxShift] = useState(8);
  const [outputName, setOutputName] = useState('');

  const [status, setStatus] = useState('');
  const [statusKind, setStatusKind] = useState('info');
  const [busy, setBusy] = useState(''); // '' | 'load' | 'generate' | 'devstart' | 'upload'
  const [lastResult, setLastResult] = useState(null);
  const [gen, setGen] = useState({ taskId: '', percent: 0, phase: '', message: '', status: '', stalled: false });

  const fileRef = useRef(null);
  const lastUpdatedRef = useRef(0);

  const loadBasics = useCallback(async () => {
    try {
      const [h, a, aud] = await Promise.all([
        getLipSyncHealth(),
        getLipSyncAvatars(),
        getLipSyncAudios(),
      ]);
      setHealth(h);
      setCaps(h?.lipsync || null);
      setGroups(a?.groups || []);
      setAudios(aud?.audios || []);
      setActiveFolder((prev) => prev || a?.groups?.[0]?.folder || '');
      if (!aud?.ok) setStatus('Servidor Lip-Sync no disponible');
    } catch (e) {
      setHealth(null); setCaps(null);
      setStatus(`Servidor Lip-Sync no disponible: ${e.message}`);
      setStatusKind('error');
    }
  }, []);

  useEffect(() => { loadBasics(); }, [loadBasics]);

  // Polling de la tarea de generación
  useEffect(() => {
    if (!gen.taskId || gen.status === 'done' || gen.status === 'error' || gen.status === 'cancelled') return;
    let stop = false;
    lastUpdatedRef.current = Date.now();
    const tick = async () => {
      try {
        const t = await getLipSyncTask(gen.taskId);
        if (stop) return;
        lastUpdatedRef.current = Date.now();
        setGen((prev) => ({
          ...prev, ...t, status: t.status,
          percent: t.progress || 0, phase: t.phase || '', message: t.message || '',
        }));
        if (t.status === 'done') {
          setLastResult(t.result || null);
          setStatus('Video generado correctamente.');
          setStatusKind('ok');
        } else if (t.status === 'error') {
          setStatus(t.error || 'Error durante la generación.');
          setStatusKind('error');
        } else if (t.status === 'cancelled') {
          setStatus('Generación cancelada.');
          setStatusKind('error');
        }
      } catch (err) {
        if (stop) return;
        if (Date.now() - lastUpdatedRef.current > 180000) {
          setStatus(`El proceso se cortó sin respuesta del servidor: ${err.message}`);
          setStatusKind('error');
        }
      }
    };
    const iv = setInterval(tick, 900);
    return () => { stop = true; clearInterval(iv); };
  }, [gen.taskId, gen.status]);

  // Aviso de "se detuvo" si el servidor no responde en 45 s
  useEffect(() => {
    if (!gen.taskId || gen.status === 'done' || gen.status === 'error' || gen.status === 'cancelled') return;
    const iv = setInterval(() => {
      if (Date.now() - lastUpdatedRef.current > 45000 && gen.taskId) {
        setGen((prev) => ({ ...prev, stalled: true }));
      }
    }, 5000);
    return () => clearInterval(iv);
  }, [gen.taskId, gen.status]);

  const handleStartServer = async () => {
    setBusy('devstart');
    setStatus('Arrancando servidor Lip-Sync…');
    setStatusKind('info');
    try {
      const r = await startLipSyncServer();
      setStatus(r.message || 'Servidor lanzado.');
      setStatusKind(r.ok ? 'info' : 'error');
      setTimeout(loadBasics, 1500);
    } catch (e) {
      setStatus(`No se pudo arrancar el servidor: ${e.message}`);
      setStatusKind('error');
    } finally {
      setBusy('');
    }
  };

  const handleRefresh = () => {
    setStatus('');
    setBusy('load');
    loadBasics()
      .catch((e) => setStatus(e.message))
      .finally(() => setBusy(''));
  };

  const handleSelectImage = (item) => {
    setImage({ name: item.name, url: item.url, path: item.path });
    setLastResult(null);
    setStatus('');
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy('upload');
    setStatus(`Subiendo ${file.name}…`);
    setStatusKind('info');
    try {
      const r = await uploadLipSyncImage(file);
      setImage({ name: r.name, url: r.url, path: r.path });
      setLastResult(null);
      setStatus('Imagen subida correctamente.');
      setStatusKind('ok');
    } catch (err) {
      setStatus(`Error al subir imagen: ${err.message}`);
      setStatusKind('error');
    } finally {
      setBusy('');
    }
  };

  const handleSelectAudio = (item) => {
    setAudio({ name: item.name, url: item.url, path: item.path, size: item.size });
    setLastResult(null);
    setStatus('');
  };

  const handleGenerate = async () => {
    if (!image) { setStatus('Selecciona primero una imagen de avatar.'); setStatusKind('error'); return; }
    if (!audio) { setStatus('Selecciona primero un audio (generado con Kokoro).'); setStatusKind('error'); return; }
    setBusy('generate');
    setStatus('');
    setLastResult(null);
    setGen({ taskId: '', percent: 0, phase: '', message: '', status: '', stalled: false });
    try {
      const g = await startLipSyncGeneration({
        image_path: image.path,
        audio_path: audio.path,
        output_name: outputName.trim() || slugify(audio.name),
        fps: Number(fps) || 25,
        bbox_shift: Number(bboxShift) || 0,
      });
      setGen({ taskId: g.task_id, percent: 0, phase: 'iniciando…', message: '', status: 'running', stalled: false });
      lastUpdatedRef.current = Date.now();
    } catch (err) {
      setStatus(`No se pudo iniciar: ${err.message}`);
      setStatusKind('error');
      setBusy('');
    }
  };

  const handleCancel = async () => {
    if (!gen.taskId) return;
    try {
      await cancelLipSyncTask(gen.taskId);
      setStatus('Cancelando…');
      setStatusKind('info');
    } catch (err) {
      setStatus(`Error al cancelar: ${err.message}`);
      setStatusKind('error');
    }
  };

  const running = gen.status === 'running' || gen.status === 'pending' || gen.status === 'cancelling';

  return (
    <div style={{ padding: '18px 20px', fontFamily: "'Inter', sans-serif", color: '#e2e8f0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '1.25rem', fontWeight: 800 }}>Vortex Studio · Lip-Sync 🎬</div>
          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Avatar + audio Kokoro → video .mp4 sincronizado (MuseTalk, GPU local)</div>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {caps && (
            <span style={S.badge(caps.ready ? '#22c55e' : '#f59e0b')}>
              {caps.ready ? 'LISTO' : 'EN ESPERA'}
              {caps.gpu?.cuda
                ? ` · GPU ${caps.gpu.device_name || ''}${caps.gpu.vram_gb ? ` ${caps.gpu.vram_gb}GB` : ''}`
                : caps.gpu?.checked
                  ? ' ⚠ CPU'
                  : ' · GPU pendiente'}
            </span>
          )}
          {DEV && (
            <button style={S.btnSecondary} onClick={handleStartServer} disabled={busy === 'devstart'}>
              {busy === 'devstart' ? 'Arrancando…' : '🚀 Arrancar servidor (1 clic)'}
            </button>
          )}
          <button style={S.btnSecondary} onClick={handleRefresh} disabled={busy === 'load'}>
            {busy === 'load' ? 'Cargando…' : '↻'}
          </button>
        </div>
      </div>

      {caps && !caps.ready && (
        <div style={{ ...S.card, border: '1px solid rgba(245,158,11,0.35)', background: 'rgba(245,158,11,0.06)' }}>
          <div style={{ fontWeight: 700, color: '#fbbf24', fontSize: '0.8rem' }}>
            ⚠️ MuseTalk (Pinokio) no está listo
          </div>
          <div style={{ fontSize: '0.78rem', color: '#e2e8f0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {(caps.reasons || []).map((r, i) => <span key={i}>• {r}</span>)}
          </div>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
            Arranca el app «TalkingMuse» en Pinokio (debe escuchar en {caps.musetalk_url || 'http://127.0.0.1:7860'}) y pulsa ↻.
          </div>
        </div>
      )}

      {status && (
        <div style={{ ...S.card, padding: '10px 14px', border: `1px solid ${statusKind === 'ok' ? 'rgba(34,197,94,0.35)' : statusKind === 'error' ? 'rgba(239,68,68,0.4)' : 'rgba(197,160,89,0.3)'}`, background: `${statusKind === 'ok' ? 'rgba(34,197,94,0.06)' : statusKind === 'error' ? 'rgba(239,68,68,0.06)' : 'rgba(197,160,89,0.05)'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '0.78rem', color: statusKind === 'error' ? '#f87171' : statusKind === 'ok' ? '#4ade80' : '#e2e8f0' }}>{status}</span>
          <button style={{ ...S.btnDanger, fontSize: '0.65rem', padding: '4px 10px' }} onClick={() => { setStatus(''); setStatusKind('info'); }}>✕</button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginTop: '14px' }} className="studio-grid">
        {/* Avatar */}
        <div style={S.card}>
          <label style={S.label}>1 · Imagen de avatar (mascota)</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ ...S.btnSecondary, flex: 1 }} onClick={() => fileRef.current?.click()} disabled={busy === 'upload'}>
              ⬆️ Subir imagen custom
            </button>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} />
          </div>
          {image && (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', background: 'rgba(7,21,38,0.6)', borderRadius: '10px', padding: '8px' }}>
              <img src={image.url} alt={image.name} style={{ width: 54, height: 54, objectFit: 'cover', borderRadius: '8px', border: '1px solid rgba(197,160,89,0.4)' }} />
              <div style={{ fontSize: '0.75rem', color: '#c5a059' }}>{image.name}</div>
            </div>
          )}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {groups.map((g) => (
              <button key={g.folder} onClick={() => setActiveFolder(g.folder)} style={{ ...S.btnSecondary, ...(activeFolder === g.folder ? { background: 'rgba(197,160,89,0.25)', color: '#f5d78c' } : {}) }}>
                📁 {g.folder} <span style={{ opacity: 0.6 }}>({g.items.length})</span>
              </button>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(58px, 1fr))', gap: '8px', maxHeight: '260px', overflowY: 'auto' }}>
            {(groups.find((g) => g.folder === activeFolder)?.items || []).map((it) => (
              <button key={it.path} onClick={() => handleSelectImage(it)} style={{ border: image?.path === it.path ? '2px solid #c5a059' : '1px solid rgba(197,160,89,0.25)', borderRadius: '8px', padding: 0, overflow: 'hidden', cursor: 'pointer', background: 'rgba(7,21,38,0.6)' }}>
                <img src={it.url} alt={it.name} style={{ width: '100%', height: '52px', objectFit: 'cover', display: 'block' }} />
              </button>
            ))}
          </div>
        </div>

        {/* Audio */}
        <div style={S.card}>
          <label style={S.label}>2 · Audio (generado con Kokoro TTS)</label>
          <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '-6px' }}>{audios.length} audios en public/audios</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <select
              style={S.select}
              value={audio?.path || ''}
              onChange={(e) => {
                const it = audios.find((a) => a.path === e.target.value);
                if (it) handleSelectAudio(it);
              }}
            >
              <option value="">— Selecciona un audio —</option>
              {audios.map((a) => (
                <option key={a.path} value={a.path}>{a.folder ? `${a.folder}/` : ''}{a.name}</option>
              ))}
            </select>
          </div>
          {audio && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', background: 'rgba(7,21,38,0.6)', borderRadius: '10px', padding: '10px' }}>
              <div style={{ fontSize: '0.75rem', color: '#c5a059', display: 'flex', justifyContent: 'space-between' }}>
                <span>{audio.name}</span>
                <span style={{ color: '#94a3b8' }}>{kb(audio.size)}</span>
              </div>
              <audio controls src={audio.url} style={{ width: '100%', height: '34px' }} />
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.4fr', gap: '10px' }}>
            <div>
              <label style={S.label}>fps</label>
              <select style={S.select} value={fps} onChange={(e) => setFps(Number(e.target.value))}>
                {[25, 30].map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>bbox_shift</label>
              <input type="number" style={S.input} value={bboxShift} onChange={(e) => setBboxShift(e.target.value)} />
            </div>
            <div>
              <label style={S.label}>Nombre del video</label>
              <input type="text" style={S.input} placeholder="destino (opcional)" value={outputName} onChange={(e) => setOutputName(e.target.value)} />
            </div>
          </div>
          <div style={{ fontSize: '0.68rem', color: '#64748b' }}>
            💡 <code>bbox_shift</code> desplaza el recorte del rostro (negativo hacia abajo) si se corta la frente. El audio se limita a 120 s por defecto.
          </div>
        </div>
      </div>

      {/* Generar */}
      <div style={{ ...S.card, marginTop: '14px', alignItems: 'stretch' }}>
        <button
          style={{ ...S.btnPrimary, justifyContent: 'center', padding: '12px', fontSize: '0.9rem' }}
          onClick={handleGenerate}
          disabled={busy === 'generate' || running || !caps?.ready}
        >
          {busy === 'generate' ? 'Iniciando…' : running ? 'Generando…' : '🎬 Generar video con lip-sync'}
        </button>

        {gen.taskId && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
              <span style={S.badge(running ? '#c5a059' : gen.status === 'error' ? '#ef4444' : gen.status === 'cancelled' ? '#94a3b8' : '#22c55e')}>
                {gen.status || 'iniciando'}
              </span>
              <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{gen.percent}% · {gen.phase}{gen.stalled ? ' · ⚠️ sin respuesta (revisa el backend)' : ''}</span>
            </div>
            <div style={{ height: '12px', borderRadius: '8px', background: 'rgba(7,21,38,0.9)', overflow: 'hidden', border: '1px solid rgba(197,160,89,0.2)' }}>
              <div style={{ height: '100%', width: `${gen.percent}%`, background: 'linear-gradient(90deg, #8a6a2e, #db2777)', transition: 'width .5s ease' }} />
            </div>
            {gen.message && <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{gen.message}</div>}
            {running && (
              <button style={{ ...S.btnDanger, alignSelf: 'flex-start' }} onClick={handleCancel}>⏹️ Cancelar</button>
            )}
          </div>
        )}
      </div>

      {/* Resultado */}
      {lastResult && (
        <div style={S.card} className="result-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={{ ...S.label, margin: 0 }}>4 · Video resultante</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <a href={lipSyncFileUrl(lastResult.path)} download={lastResult.filename}>
                <button style={S.btnSecondary}>⬇️ Descargar (.mp4)</button>
              </a>
            </div>
          </div>
          <video controls autoPlay muted loop src={lastResult.public_url || lipSyncFileUrl(lastResult.path)} title="Resultado de lip sync" aria-label="Resultado de lip sync" style={{ width: '100%', maxHeight: '360px', borderRadius: '10px', background: '#000' }} />
          <div style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <span>📄 {lastResult.filename}</span>
            <span>⏱️ {lastResult.duration_est ? `${lastResult.duration_est}s` : '—'}</span>
            <span>🎞️ {lastResult.fps} fps · ~{lastResult.frames_est} frames</span>
            <span>💾 {kb(lastResult.size)}</span>
          </div>
          <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
            El archivo queda en <code>public/lipsync/{lastResult.filename}</code> y está disponible en la web como <code>{lastResult.public_url}</code>.
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLipSync;